// Pure helpers for Create Related Listing. No React or axios imports, so this
// file is unit-testable as is.
import { isListingPlatform, type ListingPlatform } from "./listingPayload";

export interface RelatedListingSource {
  productId?: number;
  productTitle: string;
  partNumber: string;
  families: Array<{ id: number; title: string }>;
  productContact: string;
  listingTitle: string;
  platform: ListingPlatform;
}

/**
 * Prefill values from a Listings grid row (2.1.2 shape). The listing title
 * and platform come from `listings.*`; the lifted amazon_listings copies are
 * no longer served by the grid (see fieldAliasGuard.test.ts).
 */
export function shapeRelatedListingSource(row: any): RelatedListingSource {
  const rawProductId = row?.product?.product_id ?? row?.listings?.product_id;
  const productId =
    rawProductId === null || rawProductId === undefined || rawProductId === ""
      ? undefined
      : Number(rawProductId);
  const platform = row?.listings?.platform;
  const families: Array<{ id: number; title: string }> = Array.isArray(
    row?.product_families
  )
    ? row.product_families
        .filter((f: any) => f && f.value !== null && f.value !== undefined)
        .map((f: any) => ({
          id: Number(f.value),
          title: String(f.label ?? ""),
        }))
    : [];
  return {
    productId: Number.isFinite(productId) ? productId : undefined,
    productTitle: row?.product?.product_title ?? "",
    partNumber: row?.product?.assembly_part_number ?? "",
    families,
    productContact: row?.product?.product_contact ?? "",
    listingTitle: row?.listings?.listing_title ?? "",
    platform: isListingPlatform(platform) ? platform : "amazon",
  };
}

interface InventoryUnit {
  inventory_id: number | null | undefined;
  part_number?: string | null;
}

/** Distinct inventory ids in ascending order, for
 *  GET product/duplicate-check?inventoryIds=. */
export function selectedInventoryIds(
  units: ReadonlyArray<InventoryUnit>
): number[] {
  const ids = new Set<number>();
  for (const unit of units) {
    const id = Number(unit.inventory_id);
    if (
      unit.inventory_id !== null &&
      unit.inventory_id !== undefined &&
      Number.isFinite(id)
    ) {
      ids.add(id);
    }
  }
  return Array.from(ids).sort((a, b) => a - b);
}

export interface InventoryRecord {
  inventoryId: number;
  partNumber?: string;
}

/**
 * `inventoryRecords` for POST product/{id}/inventory: one record per
 * inventory id, the same shape ReviewSelectionPage sends. A missing part
 * number is left out instead of sent as null, because the API schema
 * accepts a string or nothing.
 */
export function buildInventoryRecords(
  units: ReadonlyArray<InventoryUnit>
): InventoryRecord[] {
  const byId = new Map<number, InventoryRecord>();
  for (const unit of units) {
    if (unit.inventory_id === null || unit.inventory_id === undefined) continue;
    const inventoryId = Number(unit.inventory_id);
    if (!Number.isFinite(inventoryId)) continue;
    const partNumber = unit.part_number?.trim();
    byId.set(
      inventoryId,
      partNumber ? { inventoryId, partNumber } : { inventoryId }
    );
  }
  return Array.from(byId.values());
}

export interface NewProductFields {
  title: string;
  partNumber: string;
  families: Array<{ id: number; title?: string }>;
  createdBy: string;
  productContact: string;
  createdOn: string;
}

/** Body for POST product. */
export function buildNewProductBody(fields: NewProductFields) {
  const partNumber = fields.partNumber.trim();
  return {
    product: {
      title: fields.title.trim(),
      ...(partNumber ? { partNumber } : {}),
      families: fields.families.map((f) => ({ id: f.id })),
      createdBy: fields.createdBy,
      productContact: fields.productContact,
      createdOn: fields.createdOn,
    },
  };
}

export interface MatchedProduct {
  product_id: number;
  product_title: string;
}

/** Which product the new listing goes to, for the current inventory selection. */
export type ProductMatch =
  | { status: "idle" } // nothing selected
  | { status: "checking"; key: string }
  | { status: "existing"; key: string; product: MatchedProduct }
  | { status: "new"; key: string }
  | { status: "error"; key: string; message: string };

export function matchFromDuplicateCheck(
  key: string,
  duplicateProduct: MatchedProduct | null | undefined
): ProductMatch {
  return duplicateProduct
    ? { status: "existing", key, product: duplicateProduct }
    : { status: "new", key };
}

/** True when two results send the listing to the same place. */
export function sameProductMatch(a: ProductMatch, b: ProductMatch): boolean {
  if (a.status === "existing" && b.status === "existing") {
    return Number(a.product.product_id) === Number(b.product.product_id);
  }
  return a.status === "new" && b.status === "new";
}
